/**
 * OnboardingTour
 *
 * 4-Schritte-Welcome-Dialog, der neuen Nutzer:innen die wichtigsten Funktionen
 * beim ersten Login zeigt. Wird per `localStorage` markiert, sodass er nur
 * einmal pro User erscheint. Kann jederzeit über das UserMenu erneut gestartet
 * werden.
 *
 * Steps:
 *   1. Profil vervollständigen
 *   2. Push aktivieren + App installieren
 *   3. „Wartet auf mich"-Kachel
 *   4. Erste Aktionen
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import {
  Sparkles, User, BellRing, Inbox, Rocket, ChevronRight, ChevronLeft, CheckCircle2,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const STORAGE_KEY_PREFIX = 'onboarding_completed_v1_';

const STEPS = [
  {
    icon: Sparkles,
    accent: 'from-purple-500 to-pink-500',
    title: 'Willkommen bei TeamHub!',
    description: 'In wenigen Sekunden zeigen wir dir die wichtigsten Funktionen, damit du sofort durchstarten kannst.',
    primaryAction: { label: 'Los geht\'s', icon: ChevronRight },
  },
  {
    icon: User,
    accent: 'from-blue-500 to-cyan-500',
    title: 'Dein Profil vervollständigen',
    description: 'Unter „Mein Profil" findest du deine Stammdaten, Weiterbildungen und Qualifikationen. Du kannst eigene Anträge stellen, die von deiner Leitung freigegeben werden.',
    primaryAction: { label: 'Profil öffnen', icon: ChevronRight, navigate: '/mein-profil' },
    secondaryAction: { label: 'Weiter', icon: ChevronRight },
  },
  {
    icon: BellRing,
    accent: 'from-amber-500 to-orange-500',
    title: 'Benachrichtigungen einrichten',
    description: 'Aktiviere Browser-Push, damit du Urlaubsfreigaben oder Schichtänderungen sofort siehst – auch bei geschlossener App. Installiere die App per Klick auf dein Gerät für die beste Erfahrung.',
    primaryAction: { label: 'Einrichten', icon: ChevronRight, navigate: '/mein-profil?tab=benachrichtigungen' },
    secondaryAction: { label: 'Weiter', icon: ChevronRight },
  },
  {
    icon: Inbox,
    accent: 'from-emerald-500 to-green-600',
    title: '„Wartet auf mich"-Kachel',
    description: 'Als Vorgesetzte/r siehst du auf dem Dashboard sofort alle offenen Freigaben (Urlaub, Zeitkorrekturen, Anträge). Ein Klick öffnet die passende Inbox.',
    primaryAction: { label: 'Weiter', icon: ChevronRight },
  },
  {
    icon: Rocket,
    accent: 'from-primary to-primary',
    title: 'Bereit – los geht\'s!',
    description: 'Du kannst die Tour jederzeit über das Menü oben rechts erneut starten. Viel Erfolg mit TeamHub!',
    primaryAction: { label: 'Tour beenden', icon: CheckCircle2, isFinish: true },
  },
];

const useStorageKey = () => {
  const { user } = useAuth();
  if (!user?.id) return null;
  return STORAGE_KEY_PREFIX + user.id;
};

const shouldShowOnboarding = (key) => {
  if (!key || typeof window === 'undefined') return false;
  return !window.localStorage.getItem(key);
};

const markCompleted = (key) => {
  if (key && typeof window !== 'undefined') {
    window.localStorage.setItem(key, new Date().toISOString());
  }
};

export const OnboardingTour = ({ forceOpen = false, onClose }) => {
  const key = useStorageKey();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (forceOpen) {
      setStep(0);
      setOpen(true);
    }
    // Auto-Start beim ersten Login ist auf User-Wunsch hin deaktiviert.
    // Die Tour bleibt manuell via User-Menue ("Tour neu starten") aufrufbar.
  }, [key, forceOpen]);

  const close = (completed = false) => {
    setOpen(false);
    if (completed) markCompleted(key);
    onClose?.();
  };

  const current = STEPS[step];
  const Icon = current.icon;
  const progress = ((step + 1) / STEPS.length) * 100;

  const handlePrimary = () => {
    const action = current.primaryAction;
    if (action?.navigate) {
      navigate(action.navigate);
      // Tour weiter (oder schließen), Nutzer kann später erneut öffnen
      if (step < STEPS.length - 1) {
        setStep(step + 1);
      } else {
        close(true);
      }
      return;
    }
    if (action?.isFinish || step === STEPS.length - 1) {
      close(true);
      return;
    }
    setStep(step + 1);
  };

  const handleSecondary = () => {
    if (step < STEPS.length - 1) setStep(step + 1);
    else close(true);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(false); }}>
      <DialogContent className="sm:max-w-md" onCloseAutoFocus={(e) => e.preventDefault()} data-testid="onboarding-tour">
        <DialogHeader>
          <div className={`mx-auto h-14 w-14 rounded-2xl bg-gradient-to-br ${current.accent} flex items-center justify-center mb-2 shadow-lg`}>
            <Icon className="h-7 w-7 text-white" />
          </div>
          <DialogTitle className="text-center text-xl">{current.title}</DialogTitle>
          <DialogDescription className="text-center text-sm leading-relaxed">
            {current.description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 pt-2">
          {/* Progress */}
          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Schritt {step + 1} / {STEPS.length}</span>
              <button
                type="button"
                onClick={() => close(false)}
                className="hover:underline"
                data-testid="onboarding-skip"
              >
                Überspringen
              </button>
            </div>
            <Progress value={progress} className="h-1.5" />
          </div>

          <div className="flex items-center justify-between gap-2 pt-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setStep(Math.max(0, step - 1))}
              disabled={step === 0}
              data-testid="onboarding-prev"
            >
              <ChevronLeft className="h-4 w-4 mr-1" /> Zurück
            </Button>

            <div className="flex items-center gap-2 ml-auto">
              {current.secondaryAction && (
                <Button variant="outline" size="sm" onClick={handleSecondary} data-testid="onboarding-secondary">
                  {current.secondaryAction.label}
                </Button>
              )}
              <Button size="sm" onClick={handlePrimary} data-testid="onboarding-primary">
                {current.primaryAction.label}
                {current.primaryAction.icon && (
                  <current.primaryAction.icon className="h-4 w-4 ml-1" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Markiert die Tour erneut als "nicht gesehen" und öffnet sie sofort.
 * Wird vom UserMenu „Tour erneut starten" aufgerufen.
 */
export const restartOnboardingTour = (userId) => {
  if (typeof window !== 'undefined' && userId) {
    window.localStorage.removeItem(STORAGE_KEY_PREFIX + userId);
  }
};

export default OnboardingTour;
